import React from "react";
import { Link } from "react-router-dom";

import {
  Breadcrumbs,
  DemenagementLayout,
  DictimmoCta,
  InternalLinks,
  itemListJsonLd,
  makeBreadcrumbs,
  siloLinks,
} from "./DemenagementCommon";

const path = "/demenagement";
const breadcrumbs = makeBreadcrumbs([{ name: "Déménagement", path }]);

export default function DemenagementHub() {
  const hubJsonLd = itemListJsonLd({
    name: "Outils déménagement État du Lieu",
    description: "Générateur de lettre de préavis, guide de préavis et checklists imprimables pour préparer un déménagement.",
    path,
    items: siloLinks.map((link) => ({
      name: link.title,
      path: link.to,
    })),
  });

  return (
    <DemenagementLayout
      title="Déménagement : préavis, checklists et vérification d'adresse"
      description="Préparez votre déménagement avec un générateur de lettre de préavis, des checklists imprimables et une vérification gratuite de votre future adresse."
      path={path}
      breadcrumbs={breadcrumbs}
      jsonLd={[hubJsonLd]}
    >
      <Breadcrumbs items={breadcrumbs} />

      <section className="move-hero">
        <div>
          <h1>Déménager sans mauvaise surprise</h1>
          <p>
            Préavis, visite, contrats, démarches administratives : un déménagement
            se joue souvent sur des détails. Ces outils vous aident à avancer dans
            le bon ordre, puis à vérifier l'environnement de votre future adresse
            avec Dictimmo.
          </p>
          <div className="move-hero-actions">
            <Link className="move-btn move-btn-primary" to="/outils/lettre-preavis-location">
              Générer ma lettre de préavis
            </Link>
            <Link className="move-btn move-btn-secondary" to="/checklists/checklist-demenagement-pdf">
              Voir la checklist
            </Link>
          </div>
        </div>
        <aside className="move-hero-panel" aria-label="Ce que vous pouvez préparer">
          <h2>Avant de vous engager</h2>
          <ul>
            <li>Vérifier le délai de préavis applicable à votre situation.</li>
            <li>Imprimer les listes utiles pour la visite et le déménagement.</li>
            <li>Analyser la future adresse : prix, risques, commune, environnement.</li>
          </ul>
        </aside>
      </section>

      <section aria-labelledby="move-tools-title">
        <h2 id="move-tools-title">Outils pratiques</h2>
        <div className="move-card-grid">
          {siloLinks.map((link, index) => (
            <Link className="move-card-link" to={link.to} key={link.to}>
              <span className="move-card-index">{String(index + 1).padStart(2, "0")}</span>
              <span>{link.title}</span>
              <small>{link.text}</small>
            </Link>
          ))}
        </div>
      </section>

      <section className="move-note" aria-labelledby="move-parcours-title">
        <h2 id="move-parcours-title">Quel outil utiliser selon votre situation ?</h2>
        <ul>
          <li>
            Vous quittez une location : commencez par le guide préavis, puis générez
            votre lettre de congé.
          </li>
          <li>
            Vous visitez un appartement ou une maison : utilisez la checklist visite
            logement avant de signer un bail ou de faire une offre.
          </li>
          <li>
            Votre déménagement approche : imprimez la checklist déménagement pour
            suivre préavis, assurance, énergie, courrier, compteurs et état des lieux.
          </li>
        </ul>
      </section>

      <section className="move-note" aria-labelledby="move-guides-title">
        <h2 id="move-guides-title">Avant de choisir la prochaine adresse</h2>
        <p>
          Comparez les points à vérifier <Link to="/guides/que-verifier-avant-louer-un-logement">avant de louer</Link>
          {" "}et confirmez <Link to="/guides/verifier-fibre-internet-avant-emmenager">la fibre et Internet</Link>
          {" "}avant de planifier votre installation. Retrouvez les autres sujets dans les <Link to="/guides">guides pratiques</Link>.
        </p>
      </section>

      <DictimmoCta
        title="Vous avez déjà une future adresse ? Vérifiez-la gratuitement avec Dictimmo."
        text="Prix du secteur, risques naturels, environnement, météo, services, transports et données de la commune : lancez une fiche gratuite en quelques secondes."
        buttonLabel="Vérifier ma future adresse"
        eventName="dictimmo_cta_clicked_from_demenagement"
      />

      <InternalLinks currentPath={path} />
    </DemenagementLayout>
  );
}
